import { useEffect, useState } from 'react'
import { Toaster as Sonner, type ToasterProps } from 'sonner'
import { isDark } from '@/lib/theme'

/**
 * Sonner, wired to this app's own theme control (src/lib/theme.ts) rather than
 * next-themes. Every state-changing toast should carry an Undo action:
 *   toast("Item archived", { action: { label: "Undo", onClick: revert } })
 */
/** A phone (docs/LAYOUT.md: under 768px). */
const PHONE = '(max-width: 767px)'

const Toaster = ({ ...props }: ToasterProps) => {
  const [theme, setTheme] = useState<ToasterProps['theme']>('light')
  // On a phone the bottom holds the tab bar and a popup's Cancel and Save, so a
  // toast there covers the next tap. It shows at the top, under the 64px header.
  const [phone, setPhone] = useState(() => window.matchMedia(PHONE).matches)

  useEffect(() => {
    const media = window.matchMedia(PHONE)
    const sync = () => setPhone(media.matches)
    media.addEventListener('change', sync)
    return () => media.removeEventListener('change', sync)
  }, [])

  useEffect(() => {
    const sync = () => setTheme(isDark() ? 'dark' : 'light')
    sync()

    const media = window.matchMedia('(prefers-color-scheme: dark)')
    media.addEventListener('change', sync)

    // The theme control writes data-theme on <html>; watch it so toasts follow.
    const observer = new MutationObserver(sync)
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    })

    return () => {
      media.removeEventListener('change', sync)
      observer.disconnect()
    }
  }, [])

  return (
    <Sonner
      theme={theme}
      className="toaster group"
      position={phone ? 'top-center' : 'bottom-center'}
      mobileOffset={{ top: 72 }}
      toastOptions={{
        // A toast with nothing to press (no Undo) lets taps through to what is
        // under it — on a phone it sits over the page's top controls, and one
        // that is hovered never goes away.
        classNames: {
          toast: '!pointer-events-none has-[[data-button]]:!pointer-events-auto',
        },
      }}
      style={
        {
          '--normal-bg': 'var(--popover)',
          '--normal-text': 'var(--popover-foreground)',
          '--normal-border': 'var(--border)',
          '--border-radius': 'var(--radius)',
        } as React.CSSProperties
      }
      {...props}
    />
  )
}

export { Toaster }
